import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck, ShieldOff, Copy, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function WorkerPinDialog({ sourceType, record, onClose }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [pin, setPin] = useState("");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailed, setEmailed] = useState(null);
  const open = !!record;
  const base = record ? `/time/worker-access/${sourceType}/${record.id}` : null;

  const load = () => {
    setLoadErr(null);
    api.get(base).then((r) => setInfo(r.data))
      .catch((e) => { setInfo(null); setLoadErr(formatApiErrorDetail(e.response?.data?.detail) || t("wpin.loadError")); });
  };
  useEffect(() => {
    if (!record) return;
    setPin(""); setCustom(""); setInfo(null); setEmailed(null); setLoadErr(null);
    load();
  }, [record]); // eslint-disable-line

  const refresh = () => api.get(base).then((r) => setInfo(r.data)).catch(() => {});
  const doPin = async (mode, delivery = "show") => {
    if (mode === "set" && !/^\d{6}$/.test(custom)) { toast.error(t("wpin.mustSix")); return; }
    setBusy(true);
    try {
      const r = await api.post(`${base}/pin`, { mode, delivery, pin: mode === "set" ? custom : undefined });
      if (delivery === "email") { setEmailed(r.data.email_masked); toast.success(t("wpin.emailSent")); }
      else setPin(r.data.pin);
      setCustom(""); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    setBusy(false);
  };
  const toggle = async (enable) => {
    setBusy(true);
    try { await api.post(`${base}/${enable ? "enable" : "disable"}`); refresh(); toast.success(t("wpin.saved")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="worker-pin-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-cyan-dark" /> {t("wpin.title")}</DialogTitle></DialogHeader>
        {loadErr ? (
          <div className="my-4 space-y-3 text-center" data-testid="worker-pin-error">
            <ShieldOff className="mx-auto h-6 w-6 text-red-500" />
            <p className="text-sm text-red-600">{loadErr}</p>
            <Button onClick={load} variant="outline" className="rounded-full" data-testid="worker-pin-retry">{t("wpin.retry")}</Button>
          </div>
        ) : !info ? <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">{info.worker_name} · <span className="text-slate-400">{info.worker_id}</span></div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-semibold ${info.access_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{info.access_enabled ? t("wpin.enabled") : t("wpin.disabled")}</span>
              <span className={`rounded-full px-2 py-0.5 font-semibold ${info.has_pin ? "bg-cyan/10 text-cyan-dark" : "bg-amber-100 text-amber-700"}`}>{info.has_pin ? t("wpin.hasPin") : t("wpin.noPin")}</span>
              {info.locked_until && <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">{t("wpin.locked")}</span>}
            </div>

            {pin ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="worker-pin-shown">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{t("wpin.pinShown")}</div>
                <div className="my-2 font-mono text-3xl font-extrabold tracking-widest text-navy" data-testid="worker-pin-value">{pin}</div>
                <button onClick={() => { navigator.clipboard?.writeText(pin); toast.success("✓"); }} className="text-xs text-cyan-dark hover:underline"><Copy className="mr-0.5 inline h-3.5 w-3.5" /> Copy</button>
                <p className="mt-2 text-xs text-amber-700">{t("wpin.pinOnce")}</p>
              </div>
            ) : emailed ? (
              <div className="rounded-xl border border-cyan/30 bg-cyan/5 p-4 text-center" data-testid="worker-pin-emailed">
                <Mail className="mx-auto mb-1 h-5 w-5 text-cyan-dark" />
                <div className="text-xs font-semibold uppercase tracking-wide text-cyan-dark">{t("wpin.emailSent")}</div>
                <div className="my-1 text-sm font-medium text-navy">{emailed}</div>
                <p className="mt-1 text-xs text-slate-500">{t("wpin.emailNote")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Button onClick={() => doPin("generate")} disabled={busy} className="w-full rounded-full bg-navy text-white hover:bg-navy/90" data-testid="worker-pin-generate">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1 h-4 w-4" />} {t("wpin.generate")}</Button>
                <Button onClick={() => doPin("generate", "email")} disabled={busy || !info.has_email} title={!info.has_email ? t("wpin.noEmail") : ""} variant="outline" className="w-full rounded-full border-cyan/40 text-cyan-dark hover:bg-cyan/5" data-testid="worker-pin-email">
                  <Mail className="mr-1 h-4 w-4" /> {t("wpin.generateEmail")}{info.email_masked ? ` · ${info.email_masked}` : ""}
                </Button>
                {!info.has_email && <p className="text-[11px] text-amber-600" data-testid="worker-pin-no-email">{t("wpin.noEmail")}</p>}
                <div className="flex gap-2">
                  <Input inputMode="numeric" maxLength={6} placeholder="••••••" value={custom} onChange={(e) => setCustom(e.target.value.replace(/\D/g, ""))} data-testid="worker-pin-custom" />
                  <Button onClick={() => doPin("set")} disabled={busy} variant="outline" className="rounded-full" data-testid="worker-pin-set">{t("wpin.setCustom")}</Button>
                </div>
              </div>
            )}

            <div className="border-t border-slate-100 pt-3">
              {info.access_enabled
                ? <Button onClick={() => toggle(false)} disabled={busy} variant="outline" className="w-full rounded-full text-red-600" data-testid="worker-access-disable"><ShieldOff className="mr-1 h-4 w-4" /> {t("wpin.disableAccess")}</Button>
                : <Button onClick={() => toggle(true)} disabled={busy} variant="outline" className="w-full rounded-full text-emerald-700" data-testid="worker-access-enable"><ShieldCheck className="mr-1 h-4 w-4" /> {t("wpin.enableAccess")}</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
